import type { ClassesType } from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { defineStore } from "pinia";

export const useClasses = defineStore("directory/classes", {
  state: () => {
    return {
      data: [] as ClassesType,
      loading: false,
      error: null as Error | null
    };
  },
  actions: {
    async fetch() {
      this.loading = true;
      this.error = null;
      
      try {
        // Still use the courses endpoint for backwards compatibility with the API
        const res = await FetchService.fetchData("/courses", "get");
        
        // Debug log the response
        // console.log("API response for classes:", res);
        
        // Transform the API response to work with our UI
        if (Array.isArray(res)) {
          this.data = res.map(cls => {
            const normalizedClass = {
              ...cls,
              // Add missing properties or normalize as needed
              uid: cls.uid || cls.class_id,
              deleted: cls.deleted || (cls.excluded ? cls.excluded === 1 : false),
              pinned: cls.pinned || false,
              class_name: cls.class_name || cls.title || cls.course_name ,
              class_code: cls.class_code || cls.course_code || cls.class_code
            };
            return normalizedClass;
          });
          
          // console.log("Normalized classes data:", this.data);
        } else {
          console.log("API response is not an array:", res);
          this.data = [];
        }
        
        return this.data;
      } catch (error) {
        console.error("Error fetching classes:", error);
        this.error = error instanceof Error ? error : new Error(String(error));
        this.data = [];
        return [];
      } finally {
        this.loading = false;
      }
    },
    async attach(class_id: string, relationship: string, uid: string) {
      const res = await FetchService.fetchData(`/courses/${class_id}/attach`, "put", {
        [relationship]: uid
      });
      return res;
    },
    async detach(class_id: string, relationship: string, uids: string | string[]) {
      const payload = Array.isArray(uids) ? { [relationship]: uids } : { [relationship]: [uids] };
      const res = await FetchService.fetchData(`/courses/${class_id}/detach`, "put", payload);
      return res
    },
    async getClass(uid: string) {
          if (!uid || uid === "undefined" || uid === "null") {
            return null;
          }else{
            const res = await FetchService.fetchData(`/courses/${uid}`, "get");
            return res;
          }
        },
    async linkTeacherClass(teacher_uid, class_uid) {
      const res = await FetchService.fetchData(`/courses/${class_uid}/attach`, "put", { teacher_uid });
      return res;
    },
    async linkStudentClass(student_uid, class_uid) {
      const res = await FetchService.fetchData(`/courses/${class_uid}/attach`, "put", { 
        student_uid 
      });
      return res;
    },
    async deleteTeacherClassRelations(teacher_uids, class_uid) {
      const res = await FetchService.fetchData(`/courses/detach/${class_uid}/teachers`, "put", { uids: teacher_uids });
      return res;
    },
    async deleteStudentClassRelations(student_uids, class_uid) {
      const res = await FetchService.fetchData(`/courses/detach/${class_uid}/students`, "put", {uids: student_uids} ); 
      return res;
    },
    async updateClass(class_uid, classData: Partial<ClassesType[number]>) {
      try {
        const res = await FetchService.fetchData(`/courses/${class_uid}`, "put", classData);
        
        if (res) {
          // Update the local data
          const index = this.data.findIndex(cls => cls.uid === class_uid);
          if (index !== -1) {
            this.data[index] = { ...this.data[index], ...res };
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error updating class:", error);
        return false;
      }
    },
    async createClass(classData: Partial<ClassesType[number]>) {
      try {
        const res = await FetchService.fetchData(`/courses`, "post", classData);
        
        if (res?.uid) {
          // Add to local data
          this.data.push({ ...res, deleted: false });
          return res.class_name || res.course_name as string;
        }
        return false;
      } catch (error) {
        console.error("Error creating class:", error);
        return false;
      }
    },
    async exclude() {
      // Collect all excluded class_ids as strings
      const class_ids = this.data.filter(cls => cls.excluded).map(cls => String(cls.class_id));
      try {
        const res = await FetchService.fetchData("/courses/exclude", "post", { class_ids });
        if (Array.isArray(res)) {
          this.data = res.map(cls => {
            const normalizedClass = {
              ...cls,
              uid: cls.uid || cls.class_id,
              deleted: cls.deleted || (cls.excluded ? cls.excluded === 1 : false),
              pinned: cls.pinned || false,
              class_name: cls.class_name || cls.course_name || cls.title,
              class_code: cls.class_code || cls.course_code || cls.class_codes
            };
            return normalizedClass;
          });
        }
        return this.data;
      } catch (error) {
        console.error("Error excluding classes:", error);
        return [];
      }
    },
    async include(classesToInclude) {
      // Collect all excluded class_ids as strings
      const class_ids = classesToInclude.filter(cls => !cls.excluded).map(cls => String(cls.class_id));
      try {
        const res = await FetchService.fetchData("/courses/include", "post", { class_ids });
        if (Array.isArray(res)) {
          this.data = res.map(cls => {
            const normalizedClass = {
              ...cls,
              uid: cls.uid || cls.class_id,
              deleted: cls.deleted || (cls.excluded ? cls.excluded === 1 : false),
              pinned: cls.pinned || false,
              class_name: cls.class_name || cls.course_name || cls.title,
              class_code: cls.class_code || cls.course_code || cls.class_codes
            };
            return normalizedClass;
          });
        }
        return this.data;
      } catch (error) {
        console.error("Error excluding classes:", error);
        return [];
      }
    }
  }
});

export default useClasses;